import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ClawCode.css';
import logger from './utils/logger';
import CodeHighlight from './components/CodeHighlight';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configuration constants
const STATUS_POLL_INTERVAL_MS = 30000;
const AUTO_SAVE_DEBOUNCE_MS = 1000;
const DEFAULT_CODE = '# Write your code here\n\ndef hello_world():\n    print("Hello, World!")\n\nhello_world()';

function ClawCode() {
  // Session management
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const [newName, setNewName] = useState('');

  // Code editor state
  const [code, setCode] = useState(DEFAULT_CODE);
  const [language, setLanguage] = useState('python');
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [output, setOutput] = useState(null);
  const [status, setStatus] = useState(null);
  const [showMemories, setShowMemories] = useState(false);
  const [memories, setMemories] = useState([]);

  // File upload
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  const messagesEndRef = useRef(null);
  const codeRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/clawcode/status`);
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      logger.error('Failed to fetch status:', err);
    }
  }, []);

  const fetchMemories = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/clawcode/memories?limit=10`);
      const data = await res.json();
      setMemories(data.memories || []);
    } catch (err) {
      logger.error('Failed to fetch memories:', err);
    }
  }, []);

  const selectSession = useCallback(async (session) => {
    try {
      const res = await fetch(`${API_URL}/api/clawcode/sessions/${session.id}`);
      const data = await res.json();
      setActiveSession(data);
      setCode(data.code);
      setLanguage(data.language);
      setMessages(data.messages || []);
      setOutput(data.last_output);
    } catch (err) {
      logger.error('Failed to load session:', err);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/clawcode/sessions`);
      const data = await res.json();
      setSessions(data);
      if (!activeSession && data.length > 0) {
        selectSession(data[0]);
      }
    } catch (err) {
      logger.error('Failed to fetch sessions:', err);
    }
  }, [activeSession, selectSession]);

  useEffect(() => {
    fetchStatus();
    fetchSessions();
    const interval = setInterval(fetchStatus, STATUS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchStatus, fetchSessions]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Auto-save on code/language change
  useEffect(() => {
    if (!activeSession) return;

    const autoSave = setTimeout(async () => {
      try {
        await fetch(`${API_URL}/api/clawcode/sessions/${activeSession.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, language })
        });
      } catch (err) {
        logger.error('Auto-save failed:', err);
      }
    }, AUTO_SAVE_DEBOUNCE_MS);

    return () => clearTimeout(autoSave);
  }, [code, language, activeSession]);

  const createSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/clawcode/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `Session ${sessions.length + 1}`,
          code: DEFAULT_CODE,
          language: 'python'
        })
      });
      const session = await res.json();
      setSessions([session, ...sessions]);
      setActiveSession(session);
      setCode(session.code);
      setLanguage(session.language);
      setMessages(session.messages || []);
      setOutput(session.last_output);
    } catch (err) {
      logger.error('Failed to create session:', err);
    }
  };

  const deleteSession = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    try {
      await fetch(`${API_URL}/api/clawcode/sessions/${id}`, { method: 'DELETE' });
      const newSessions = sessions.filter(s => s.id !== id);
      setSessions(newSessions);
      if (activeSession?.id === id) {
        if (newSessions.length > 0) {
          selectSession(newSessions[0]);
        } else {
          setActiveSession(null);
          setCode(DEFAULT_CODE);
          setLanguage('python');
          setMessages([]);
          setOutput(null);
        }
      }
    } catch (err) {
      logger.error('Failed to delete session:', err);
    }
  };

  const updateSessionName = async (id, name) => {
    try {
      await fetch(`${API_URL}/api/clawcode/sessions/${id}/name?name=${encodeURIComponent(name)}`, {
        method: 'PUT'
      });
      setSessions(sessions.map(s => s.id === id ? { ...s, name } : s));
      if (activeSession?.id === id) {
        setActiveSession({ ...activeSession, name });
      }
      setEditingName(null);
    } catch (err) {
      logger.error('Failed to update name:', err);
    }
  };

  const sendMessage = async () => {
    if (!query.trim() || loading) return;

    const userMessage = query.trim();
    let fileContext = '';

    if (uploadedFile && uploadedFile.attachToChat) {
      fileContext = `\n\nAttached file (${uploadedFile.filename}):\n\`\`\`\n${uploadedFile.content}\n\`\`\``;
    }

    setQuery('');
    setUploadedFile(null);
    setLoading(true);

    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      const res = await fetch(`${API_URL}/api/clawcode/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          language,
          query: userMessage + fileContext,
          session_id: activeSession?.id
        })
      });

      const data = await res.json();

      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (err) {
      logger.error('Chat error:', err);
      alert(`Error: ${err.message}`);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const executeCode = async () => {
    setExecuting(true);
    setOutput(null);
    try {
      const res = await fetch(`${API_URL}/api/clawcode/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, language })
      });
      const data = await res.json();
      setOutput(data);

      if (activeSession) {
        await fetch(`${API_URL}/api/clawcode/sessions/${activeSession.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ last_output: data })
        });
      }
    } catch (err) {
      setOutput({ success: false, error: err.message });
    }
    setExecuting(false);
  };

  const applyCodeFromMessage = (codeBlock) => {
    const codeMatch = codeBlock.match(/```(\w+)?\n([\s\S]*?)```/);
    if (codeMatch) {
      const lang = codeMatch[1] || 'python';
      const codeContent = codeMatch[2];
      setCode(codeContent);
      if (['python', 'javascript', 'typescript'].includes(lang)) {
        setLanguage(lang);
      }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/api/clawcode/upload`, {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Upload failed');
      }

      const data = await res.json();
      setUploadedFile({ ...data, attachToChat: false });

      if (data.truncated) {
        alert('File was truncated to 100KB due to size limits.');
      }
    } catch (err) {
      logger.error('Upload error:', err);
      alert(`Failed to upload: ${err.message}`);
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const loadFileToEditor = () => {
    if (!uploadedFile) return;
    setCode(uploadedFile.content);
    setUploadedFile(null);
  };

  const attachFileToChat = () => {
    if (!uploadedFile) return;
    setUploadedFile({ ...uploadedFile, attachToChat: true });
  };

  // Safe message renderer - splits on code fences and uses SyntaxHighlighter React component
  const renderMessage = (msg) => {
    const parts = msg.content.split(/(```[\s\S]*?```)/);
    return parts.map((part, idx) => {
      const codeMatch = part.match(/```(\w+)?\n?([\s\S]*?)```/);
      if (codeMatch) {
        const lang = codeMatch[1] || 'python';
        const codeContent = codeMatch[2];
        return (
          <div key={`code-block-${idx}`} className="code-block" data-testid="message-code-block">
            <div className="code-block-header">
              <span>{lang}</span>
              <button onClick={() => applyCodeFromMessage(part)} data-testid="apply-code-btn">
                Apply to Editor
              </button>
            </div>
            <CodeHighlight code={codeContent} language={lang} />
          </div>
        );
      }
      return <span key={`text-${idx}`}>{part}</span>;
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="clawcode" data-testid="clawcode">
      {/* Session Sidebar */}
      <div className="thread-sidebar">
        <button className="new-thread-btn" onClick={createSession} data-testid="new-session-btn">
          <span>+</span> New Session
        </button>
        <div className="threads-list">
          {sessions.map(session => (
            <div
              key={session.id}
              className={`thread-item ${activeSession?.id === session.id ? 'active' : ''}`}
            >
              {editingName === session.id ? (
                <input
                  className="thread-name-input"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onBlur={() => updateSessionName(session.id, newName)}
                  onKeyDown={(e) => e.key === 'Enter' && updateSessionName(session.id, newName)}
                  autoFocus
                />
              ) : (
                <>
                  <span
                    className="thread-name"
                    onClick={() => selectSession(session)}
                    onDoubleClick={() => {
                      setEditingName(session.id);
                      setNewName(session.name);
                    }}
                  >
                    {session.name}
                  </span>
                  <button
                    className="delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSession(session.id);
                    }}
                  >
                    ×
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="clawcode-main">
        {/* Header */}
        <div className="clawcode-header">
          <div className="clawcode-title">
            <span className="claw-icon">🐾</span>
            <h2>ClawCode IDE</h2>
            {activeSession && <span className="session-name">{activeSession.name}</span>}
          </div>
          <div className="status-bar">
            {status && (
              <>
                <span className="status-item">⚡ Kairos: {status.kairos?.active_tasks || 0} tasks</span>
                <span className="status-item">🧠 Memories: {status.autodream?.memory_count || 0}</span>
                <button
                  className="memories-btn"
                  onClick={() => {
                    setShowMemories(!showMemories);
                    if (!showMemories) fetchMemories();
                  }}
                >
                  {showMemories ? 'Hide' : 'Show'} Memories
                </button>
              </>
            )}
          </div>
        </div>

        <div className="clawcode-content">
          {/* Left: Code Editor */}
          <div className="code-section">
            <div className="code-controls">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="language-select"
                data-testid="language-select"
              >
                <option value="python">Python</option>
                <option value="javascript">JavaScript</option>
                <option value="typescript">TypeScript</option>
              </select>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".py,.js,.ts,.html,.css,.json,.txt,.md,.jsx,.tsx,.java,.cpp,.c,.h,.go,.rs,.rb,.php,.sh"
                style={{ display: 'none' }}
              />
              <button
                className="upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                title="Upload file"
                data-testid="upload-file-btn"
              >
                {uploadingFile ? '...' : '📁 Upload File'}
              </button>

              <button
                onClick={executeCode}
                disabled={executing || !code}
                className="run-btn"
                data-testid="run-btn"
              >
                {executing ? '⏳ Running...' : '▶ Run Code'}
              </button>
            </div>

            {/* File Upload Actions */}
            {uploadedFile && !uploadedFile.attachToChat && (
              <div className="uploaded-file-actions">
                <span className="file-name">📎 {uploadedFile.filename}</span>
                <button onClick={loadFileToEditor} className="action-btn" data-testid="load-to-editor-btn">Load to Editor</button>
                <button onClick={attachFileToChat} className="action-btn" data-testid="attach-to-chat-btn">Attach to Chat</button>
                <button onClick={() => setUploadedFile(null)} className="remove-btn">×</button>
              </div>
            )}

            <div className="editor-container">
              <textarea
                ref={codeRef}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="code-editor"
                spellCheck={false}
                data-testid="code-editor"
              />
              <div className="code-highlight" aria-hidden="true">
                <CodeHighlight code={code} language={language} />
              </div>
            </div>

            {/* Output Panel */}
            {output && (
              <div className={`output-panel ${output.success ? 'success' : 'error'}`} data-testid="output-panel">
                <div className="output-header">
                  <span>{output.success ? '✅ Output' : '❌ Error'}</span>
                  <button onClick={() => setOutput(null)}>×</button>
                </div>
                <pre className="output-content">
                  {output.stdout || output.stderr || output.error || 'No output'}
                </pre>
              </div>
            )}
          </div>

          {/* Right: AI Chat */}
          <div className="chat-section">
            <div className="chat-header">
              <h3>AI Assistant</h3>
            </div>

            <div className="chat-messages" data-testid="chat-messages">
              {messages.map((msg, i) => (
                <div key={`msg-${i}`} className={`message ${msg.role}`}>
                  <div className="message-role">
                    {msg.role === 'user' ? 'You' : 'ClawCode'}
                  </div>
                  <div className="message-content">
                    {renderMessage(msg)}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="message assistant loading">
                  <div className="message-role">ClawCode</div>
                  <div className="message-content">
                    <span className="typing-indicator">
                      <span></span><span></span><span></span>
                    </span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-area">
              {uploadedFile?.attachToChat && (
                <div className="attached-file">
                  <span className="file-icon">📎</span>
                  <span className="file-name">{uploadedFile.filename} (attached)</span>
                  <button className="remove-file-btn" onClick={() => setUploadedFile(null)}>×</button>
                </div>
              )}
              <textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask ClawCode anything..."
                disabled={loading}
                rows={3}
                className="chat-input"
                data-testid="query-input"
              />
              <button
                className="send-btn"
                onClick={sendMessage}
                disabled={loading || !query.trim()}
                data-testid="send-btn"
              >
                {loading ? '...' : 'Send'}
              </button>
            </div>
          </div>
        </div>

        {/* Memories Panel */}
        {showMemories && (
          <div className="memories-panel" data-testid="memories-panel">
            <h3>Recent Memories</h3>
            <div className="memories-list">
              {memories.map((mem, i) => (
                <div key={`mem-${i}`} className="memory-item">
                  <div className="memory-content">{mem.content?.substring(0, 100)}...</div>
                  <div className="memory-meta">
                    <span>Type: {mem.context_type}</span>
                    <span>Importance: {(mem.importance * 100).toFixed(0)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClawCode;
